import React, { useState, useEffect } from "react";
import { Copy, Key, Eye, EyeOff, Trash2, Globe, ShieldCheck, Zap, Clock } from "lucide-react";
import ApiClient from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";

const WebhookManagement = ({ activeAgentId }) => {
  const { showToast } = useToast();
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [showKeys, setShowKeys] = useState({});
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadApiKeys();
  }, [activeAgentId]);

  const loadApiKeys = async () => {
    try {
      const response = await ApiClient.request("/webhook/keys");
      setApiKeys(response.keys || []);
    } catch (error) {
      console.error("Failed to load API keys:", error);
    }
  };



  const createApiKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const response = await ApiClient.request("/webhook/keys", {
        method: "POST",
        body: JSON.stringify({ key_name: newKeyName }),
      });
      
      if (response.api_key) {
        showToast(`API Key "${newKeyName}" created successfully!`, "success");
      } else {
        showToast(`API Key "${newKeyName}" created successfully!`, "success");
      }
      
      setNewKeyName("");
      loadApiKeys();
    } catch (error) {
      showToast(`Failed: ${error.message || error}`, "error");
    }
  };

  const toggleKey = async (keyId, currentStatus) => {
    try {
      await ApiClient.request(`/webhook/keys/${keyId}`, {
        method: "PUT",
        body: JSON.stringify({ is_active: !currentStatus }),
      });
      loadApiKeys();
      showToast("Status updated");
    } catch (error) {
      showToast("Failed to toggle API key", "error");
    }
  };

  const deleteKey = (keyId) => {
    setKeyToDelete(keyId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!keyToDelete) return;
    setIsDeleting(true);
    try {
      await ApiClient.request(`/webhook/keys/${keyToDelete}`, {
        method: "DELETE",
      });
      loadApiKeys();
      showToast("API key permanently deleted", "success");
      setIsDeleteModalOpen(false);
    } catch (error) {
      showToast("Failed to delete API key", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const copyToClipboard = (text, type = "key") => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === "url") {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      }
      showToast(`${type === "url" ? "URL" : "API Key"} copied to clipboard!`);
    });
  };

  const webhookUrl = `${process.env.REACT_APP_API_BASE_URL}/webhook/incoming`;

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-12">
        <PageHeader 
          title="Neural Webhooks"
          description="Orchestrate real-time data flows between your AI ecosystem and the global digital web."
        />

        <div className="grid gap-8 animate-slideIn">
          {/* Webhook Endpoint Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <Globe size={24} className="text-blue-600" />
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Incoming Webhook</h3>
                <p className="text-[11px] font-medium text-gray-400">Universal endpoint for external data delivery</p>
              </div>
            </div>

            <div className="p-6 bg-gray-50 rounded-[24px] border border-gray-100 group transition-all hover:bg-white hover:shadow-inner">
              <div className="flex items-center justify-between gap-4">
                <div className="font-mono text-xs text-gray-600 truncate flex-1 leading-loose">
                  {webhookUrl}
                </div>
                <button
                  onClick={() => copyToClipboard(webhookUrl, "url")}
                  className={`p-2 rounded-lg transition-all ${
                    copiedUrl ? "text-blue-500" : "text-gray-400 hover:text-gray-900"
                  }`}
                  title={copiedUrl ? "Copied!" : "Copy URL"}
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>
            <p className="mt-4 text-[10px] text-gray-400 leading-relaxed px-2">
              All incoming POST requests to this endpoint will be processed. Ensure you include a valid `Authorization: Bearer YOUR_API_KEY` header in your requests.
            </p>
          </div>

          {/* API Key Creation Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <Key size={24} className="text-gray-900" />
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Access Control</h3>
                <p className="text-[11px] font-medium text-gray-400">Generate secure credentials for your data streams</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="w-full flex-1 group">
                <input
                  type="text"
                  placeholder="Service Name (e.g. Stripe, Custom App)"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="w-full px-5 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-medium text-xs transition-all shadow-inner"
                  onKeyPress={(e) => e.key === "Enter" && createApiKey()}
                />
              </div>
              <button
                onClick={createApiKey}
                disabled={!newKeyName.trim()}
                className="flex items-center justify-center gap-2 px-10 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all font-bold text-xs uppercase tracking-widest shadow-md shadow-gray-200 active:scale-95"
              >
                Create Key
              </button>
            </div>
          </div>

          {/* API Keys List Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50">
            <div className="flex items-center gap-3 mb-8">
              <ShieldCheck size={24} className="text-amber-600" />
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Credential Vault</h3>
                <p className="text-[11px] font-medium text-gray-400">{apiKeys.length} keys active in rotation</p>
              </div>
            </div>

            {apiKeys.length === 0 ? (
              <div className="text-center py-16 bg-gray-50/50 rounded-[24px] border border-gray-100 border-dashed">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100">
                  <Key size={28} className="text-gray-300" />
                </div>
                <p className="text-gray-400 font-medium">No API keys generated yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="group relative p-6 bg-white border border-gray-100 rounded-[24px] transition-all hover:shadow-lg hover:border-gray-200"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <Key size={22} className={key.is_active ? "text-blue-600" : "text-gray-400"} />
                        <div>
                          <h4 className="font-bold text-gray-900">{key.key_name}</h4>
                          <div className="flex items-center gap-2 text-[10px] text-gray-400 font-bold tracking-wider uppercase">
                            <Clock size={10} />
                            CREATED {new Date(key.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex-1 md:w-96 relative min-w-0">
                          <div className={`font-mono text-[11px] bg-gray-50 pl-4 pr-10 py-2.5 rounded-xl border border-gray-100 text-gray-500 tracking-widest group-hover:bg-white transition-colors ${showKeys[key.id] ? "break-all" : "truncate"}`}>
                            {showKeys[key.id] ? key.api_key : "••••••••••••••••••••••••••••••••"}
                          </div>
                          <button
                            onClick={() => copyToClipboard(key.api_key, "key")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-900"
                          >
                            <Copy size={12} />
                          </button>
                        </div>

                        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                          <button
                            onClick={() => toggleKey(key.id, key.is_active)}
                            className={`p-2.5 rounded-xl border transition-all ${key.is_active ? "bg-blue-50 border-blue-100 text-blue-600" : "bg-white border-gray-100 text-gray-400 hover:text-gray-900 hover:border-gray-200"
                              }`}
                            title={key.is_active ? "Deactivate Key" : "Activate Key"}
                          >
                            <Zap size={16} />
                          </button>
                          <button
                            onClick={() => setShowKeys(prev => ({ ...prev, [key.id]: !prev[key.id] }))}
                            className={`p-2.5 rounded-xl border transition-all ${showKeys[key.id] ? "bg-amber-50 border-amber-100 text-amber-600" : "bg-white border-gray-100 text-gray-400 hover:text-gray-900 hover:border-gray-200"
                              }`}
                          >
                            {showKeys[key.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => deleteKey(key.id)}
                            className="p-2.5 bg-white border border-gray-100 text-gray-400 hover:text-red-600 hover:bg-red-50 hover:border-red-100 rounded-xl transition-all"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>


      </div>
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Purge Neural Webhook?"
        message="This action will permanently terminate this data protocol. Are you prepared to proceed with the deletion?"
      />
    </div>
  );
};

export default WebhookManagement;
